import React, { useState, useEffect } from 'react';
import Header from '../../components/navigation/Header';
import GlobalFilterBar from '../../components/navigation/GlobalFilterBar';
import ComplianceAlertBanner from '../../components/navigation/ComplianceAlertBanner';
import DataRefreshIndicator from '../../components/navigation/DataRefreshIndicator';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import billingService from '../../services/billingService';
import stripePaymentService from '../../services/stripePaymentService';
import FinancialSummaryCard from './components/FinancialSummaryCard';
import PaymentTrackingTable from './components/PaymentTrackingTable';
import SubscriptionLifecyclePanel from './components/SubscriptionLifecyclePanel';
import RevenueAnalyticsPanel from './components/RevenueAnalyticsPanel';

const SubscriptionPaymentMonitoringCenter = () => {
  const [activeTab, setActiveTab] = useState('payments');
  const [invoices, setInvoices] = useState([]);
  const [stripeInvoices, setStripeInvoices] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [statistics, setStatistics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [globalFilter, setGlobalFilter] = useState(null);
  const [dateRange, setDateRange] = useState('30days');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError('');

      // Get active company
      const company = await billingService?.getActiveCompany();

      if (company?.id) {
        const [invoicesData, stripeInvoicesData, statsData] = await Promise.all([
          billingService?.getInvoiceHistory(company?.id),
          stripePaymentService?.getInvoices(company?.id),
          billingService?.getPaymentStatistics(company?.id)
        ]);

        setInvoices(invoicesData);
        setStripeInvoices(stripeInvoicesData);
        setStatistics(statsData);
      }
    } catch (err) {
      setError(err?.message || 'Failed to load payment monitoring data');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    loadData();
  };

  const handleExport = () => {
    console.log('Export financial data');
    alert('Export functionality will generate comprehensive financial reports');
  };

  const tabs = [
    { id: 'payments', label: 'Payment Tracking', icon: 'CreditCard' },
    { id: 'subscriptions', label: 'Subscription Management', icon: 'Users' },
    { id: 'analytics', label: 'Revenue Analytics', icon: 'TrendingUp' }
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <GlobalFilterBar onFilterChange={setGlobalFilter} />
        <ComplianceAlertBanner />
        <main className="container mx-auto px-4 py-8">
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <Icon name="Loader2" size={48} className="animate-spin text-primary mx-auto mb-4" />
              <p className="text-muted-foreground">Loading payment data...</p>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <GlobalFilterBar onFilterChange={setGlobalFilter} />
      <ComplianceAlertBanner />
      <DataRefreshIndicator />

      <main className="container mx-auto px-4 py-8">
        {/* Page Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold text-foreground mb-2">Subscription & Payment Monitoring Center</h1>
            <p className="text-muted-foreground">
              Comprehensive financial oversight for tracking subscriptions, payments, and revenue analytics
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Button variant="outline" onClick={handleRefresh}>
              <Icon name="RefreshCw" size={16} className="mr-2" />
              Refresh
            </Button>
            <Button onClick={handleExport}>
              <Icon name="Download" size={16} className="mr-2" />
              Export Report
            </Button>
          </div>
        </div>

        {error && (
          <div className="bg-error/10 border border-error/20 rounded-lg p-4 mb-6">
            <div className="flex items-center gap-2">
              <Icon name="AlertCircle" size={20} className="text-error" />
              <p className="text-error font-medium">{error}</p>
            </div>
          </div>
        )}

        {/* Financial Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
          <FinancialSummaryCard
            title="Monthly Recurring Revenue"
            value={`K${(statistics?.totalSpent || 0)?.toLocaleString()}`}
            subtitle="Active subscriptions"
            icon="DollarSign"
            trend="up"
            status="success"
          />
          <FinancialSummaryCard
            title="Payment Success Rate"
            value="98.5%"
            subtitle="Last 30 days"
            icon="CheckCircle"
            trend="up"
            status="success"
          />
          <FinancialSummaryCard
            title="Subscription Churn"
            value="2.3%"
            subtitle="Monthly rate"
            icon="TrendingDown"
            trend="down"
            status="warning"
          />
          <FinancialSummaryCard
            title="Overdue Accounts"
            value={statistics?.overdueInvoices || 0}
            subtitle="Requiring attention"
            icon="AlertTriangle"
            trend="down"
            status={statistics?.overdueInvoices > 0 ? 'error' : 'success'}
          />
        </div>

        {/* Tabs */}
        <div className="bg-card rounded-lg shadow-sm border border-border mb-6">
          <div className="border-b border-border">
            <div className="flex items-center gap-1 p-2">
              {tabs?.map((tab) => (
                <button
                  key={tab?.id}
                  onClick={() => setActiveTab(tab?.id)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-md transition-colors ${
                    activeTab === tab?.id
                      ? 'bg-primary text-primary-foreground'
                      : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                  }`}
                >
                  <Icon name={tab?.icon} size={16} />
                  <span className="text-sm font-medium">{tab?.label}</span>
                </button>
              ))}
            </div>
          </div>

          <div className="p-6">
            {/* Payment Tracking Tab */}
            {activeTab === 'payments' && (
              <div>
                <div className="mb-4">
                  <h2 className="text-xl font-bold text-foreground mb-2">Payment Transaction Records</h2>
                  <p className="text-sm text-muted-foreground">
                    Chronological payment records with processing status and automated retry schedules
                  </p>
                </div>
                <PaymentTrackingTable invoices={stripeInvoices} />
              </div>
            )}

            {/* Subscription Management Tab */}
            {activeTab === 'subscriptions' && (
              <div>
                <div className="mb-4">
                  <h2 className="text-xl font-bold text-foreground mb-2">Subscription Lifecycle Tracking</h2>
                  <p className="text-sm text-muted-foreground">
                    Renewal dates, upgrade/downgrade activities, and cancellation patterns
                  </p>
                </div>
                <SubscriptionLifecyclePanel invoices={invoices} />
              </div>
            )}

            {/* Revenue Analytics Tab */}
            {activeTab === 'analytics' && (
              <div>
                <div className="mb-4">
                  <h2 className="text-xl font-bold text-foreground mb-2">Revenue Analytics & Insights</h2>
                  <p className="text-sm text-muted-foreground">
                    Subscription trends, client lifetime value, and growth projections
                  </p>
                </div>
                <RevenueAnalyticsPanel statistics={statistics} invoices={invoices} />
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default SubscriptionPaymentMonitoringCenter;